import React, { useState, useEffect, useCallback } from 'react';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { InputSwitch } from 'primereact/inputswitch';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const Automation = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingReport, setEditingReport] = useState(null);

  const [reportForm, setReportForm] = useState({
    name: '',
    enabled: false,
    emails: [], // Array of emails
    emailsText: '', // Textarea input
    frequency: 'daily',
    time: '09:00',
    dayOfWeek: 1, // Monday (0 = Sunday, 1 = Monday, ...)
    dayOfMonth: 1, // 1-31
    includePDF: true,
    includeSummary: true,
    includeLevelBreakdown: true,
    includeTopUsers: true,
    includeTopModules: true,
    includeErrorStats: true
  });

  const frequencyOptions = [
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Monthly', value: 'monthly' }
  ];

  const dayOfWeekOptions = [
    { label: 'Sunday', value: 0 },
    { label: 'Monday', value: 1 },
    { label: 'Tuesday', value: 2 },
    { label: 'Wednesday', value: 3 },
    { label: 'Thursday', value: 4 },
    { label: 'Friday', value: 5 },
    { label: 'Saturday', value: 6 }
  ];

  const dayOfMonthOptions = Array.from({ length: 31 }, (_, i) => ({
    label: `${i + 1}${i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'}`,
    value: i + 1
  }));

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  };

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_BASE_URL}/automation/reports`, getAuthHeaders());
      setReports(response.data.reports || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch automation reports');
      console.error('Failed to fetch reports:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const parseEmails = (text) => {
    return text
      .split(/[,\n]/)
      .map(email => email.trim())
      .filter(email => email.length > 0);
  };

  const validateEmails = (emails) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const email of emails) {
      if (!emailRegex.test(email)) {
        return { valid: false, message: `Invalid email format: ${email}` };
      }
    }
    return { valid: true };
  };

  const resetForm = () => {
    setReportForm({
      name: '',
      enabled: false,
      emails: [],
      emailsText: '',
      frequency: 'daily',
      time: '09:00',
      dayOfWeek: 1,
      dayOfMonth: 1,
      includePDF: true,
      includeSummary: true,
      includeLevelBreakdown: true,
      includeTopUsers: true,
      includeTopModules: true,
      includeErrorStats: true
    });
    setEditingReport(null);
  };

  const handleAdd = () => {
    resetForm();
    setDialogVisible(true);
  };

  const handleEdit = (report) => {
    setEditingReport(report);
    setReportForm({
      name: report.name || '',
      enabled: report.enabled || false,
      emails: report.emails || [],
      emailsText: (report.emails || []).join(',\n'),
      frequency: report.frequency || 'daily',
      time: report.time || '09:00',
      dayOfWeek: report.dayOfWeek !== undefined ? report.dayOfWeek : 1,
      dayOfMonth: report.dayOfMonth !== undefined ? report.dayOfMonth : 1,
      includePDF: report.includePDF !== false,
      includeSummary: report.includeSummary !== false,
      includeLevelBreakdown: report.includeLevelBreakdown !== false,
      includeTopUsers: report.includeTopUsers !== false,
      includeTopModules: report.includeTopModules !== false,
      includeErrorStats: report.includeErrorStats !== false
    });
    setDialogVisible(true);
  };

  const handleDelete = (report) => {
    confirmDialog({
      message: `Are you sure you want to delete the report "${report.name || 'Untitled'}"?`,
      header: 'Delete Report',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await axios.delete(`${API_BASE_URL}/automation/reports/${report.id}`, getAuthHeaders());
          setSuccess('Report deleted successfully!');
          setTimeout(() => setSuccess(''), 3000);
          fetchReports();
        } catch (err) {
          setError(err.response?.data?.message || 'Failed to delete report');
        }
      }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    // Parse emails from textarea
    const emails = parseEmails(reportForm.emailsText);
    
    // Validation
    if (!reportForm.name.trim()) {
      setError('Report name is required');
      setSaving(false);
      return;
    }

    if (reportForm.enabled) {
      if (emails.length === 0) {
        setError('At least one email is required when automation is enabled');
        setSaving(false);
        return;
      }
      
      const emailValidation = validateEmails(emails);
      if (!emailValidation.valid) {
        setError(emailValidation.message);
        setSaving(false);
        return;
      }

      if (!reportForm.time || !/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(reportForm.time)) {
        setError('Invalid time format. Use HH:mm format (e.g., 09:00)');
        setSaving(false);
        return;
      }
    }

    try {
      const reportData = {
        name: reportForm.name,
        enabled: reportForm.enabled,
        emails: emails,
        frequency: reportForm.frequency,
        time: reportForm.time,
        dayOfWeek: reportForm.frequency === 'weekly' ? reportForm.dayOfWeek : undefined,
        dayOfMonth: reportForm.frequency === 'monthly' ? reportForm.dayOfMonth : undefined,
        includePDF: reportForm.includePDF,
        includeSummary: reportForm.includeSummary,
        includeLevelBreakdown: reportForm.includeLevelBreakdown,
        includeTopUsers: reportForm.includeTopUsers,
        includeTopModules: reportForm.includeTopModules,
        includeErrorStats: reportForm.includeErrorStats
      };

      if (editingReport) {
        await axios.put(`${API_BASE_URL}/automation/reports/${editingReport.id}`, { report: reportData }, getAuthHeaders());
        setSuccess('Report updated successfully!');
      } else {
        await axios.post(`${API_BASE_URL}/automation/reports`, { report: reportData }, getAuthHeaders());
        setSuccess('Report created successfully!');
      }

      setTimeout(() => setSuccess(''), 3000);
      setDialogVisible(false);
      resetForm();
      fetchReports();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  const handleTestReport = async (report) => {
    const emails = report.emails || [];
    if (emails.length === 0) {
      setError('No emails configured for this report');
      return;
    }

    const testEmail = emails[0]; // Send test to first email
    
    setTesting({ ...testing, [report.id]: true });
    setError('');
    setSuccess('');

    try {
      await axios.post(
        `${API_BASE_URL}/automation/reports/${report.id}/test`,
        { email: testEmail },
        getAuthHeaders()
      );
      setSuccess(`Test report sent successfully to ${testEmail}!`);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send test report');
    } finally {
      setTesting({ ...testing, [report.id]: false });
    }
  };

  const formatFrequency = (report) => {
    if (!report.enabled) return 'Disabled';
    
    let freq = report.frequency === 'daily' ? 'Daily' : 
               report.frequency === 'weekly' ? 'Weekly' : 'Monthly';
    
    if (report.frequency === 'weekly' && report.dayOfWeek !== undefined) {
      const day = dayOfWeekOptions.find(d => d.value === report.dayOfWeek);
      freq += ` (${day ? day.label : 'Unknown'})`;
    }
    
    if (report.frequency === 'monthly' && report.dayOfMonth !== undefined) {
      const day = dayOfMonthOptions.find(d => d.value === report.dayOfMonth);
      freq += ` (${day ? day.label : 'Unknown'})`;
    }
    
    return `${freq} at ${report.time || 'N/A'}`;
  };

  const statusBodyTemplate = (rowData) => {
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0.375rem 0.875rem',
        backgroundColor: rowData.enabled ? '#d4edda' : '#f8d7da',
        color: rowData.enabled ? '#155724' : '#721c24',
        borderRadius: '16px',
        fontSize: '0.8125rem',
        fontWeight: '600',
        letterSpacing: '0.025em',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
      }}>
        {rowData.enabled ? '✓ Enabled' : '✗ Disabled'}
      </span>
    );
  };

  const emailsBodyTemplate = (rowData) => {
    const emails = rowData.emails || [];
    if (emails.length === 0) return <span style={{ color: '#6c757d' }}>No emails</span>;
    if (emails.length === 1) return <span>{emails[0]}</span>;
    return <span>{emails.length} email{emails.length > 1 ? 's' : ''}</span>;
  };

  const actionsBodyTemplate = (rowData) => {
    return (
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <Button
          icon="pi pi-send"
          className="p-button-rounded p-button-text p-button-sm p-button-info"
          onClick={() => handleTestReport(rowData)}
          tooltip="Send Test Report"
          tooltipOptions={{ position: 'top' }}
          disabled={loading || testing[rowData.id]}
          loading={testing[rowData.id]}
        />
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-sm"
          onClick={() => handleEdit(rowData)}
          tooltip="Edit"
          tooltipOptions={{ position: 'top' }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger p-button-sm"
          onClick={() => handleDelete(rowData)}
          tooltip="Delete"
          tooltipOptions={{ position: 'top' }}
        />
      </div>
    );
  };

  if (loading && reports.length === 0) {
    return (
      <div style={{ 
        padding: '2rem', 
        maxWidth: '1400px', 
        margin: '0 auto',
        minHeight: 'calc(100vh - 100px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '2rem', 
      maxWidth: '1400px', 
      margin: '0 auto',
      minHeight: 'calc(100vh - 100px)'
    }}>
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ 
            marginBottom: '0.5rem', 
            fontSize: '2rem', 
            fontWeight: '600',
            color: '#2c3e50'
          }}>
            Automation
          </h1>
          <p style={{ 
            marginBottom: 0, 
            color: '#6c757d',
            fontSize: '0.95rem'
          }}>
            Configure automated usage reports with PDF attachments
          </p>
        </div>
        <Button
          label="Add Report"
          icon="pi pi-plus"
          iconPos="left"
          onClick={handleAdd}
          className="p-button-lg"
          style={{ 
            padding: '0.75rem 2rem',
            fontSize: '1rem',
            fontWeight: '500'
          }}
        />
      </div>

      {error && (
        <Message 
          severity="error" 
          text={error} 
          style={{ marginBottom: '1.5rem' }} 
          onClose={() => setError('')} 
        />
      )}

      {success && (
        <Message 
          severity="success" 
          text={success} 
          style={{ marginBottom: '1.5rem' }} 
          onClose={() => setSuccess('')} 
        />
      )}

      <Card style={{ 
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)', 
        padding: '2rem'
      }}>
        {reports.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#6c757d' }}>
            <i className="pi pi-inbox" style={{ fontSize: '4rem', marginBottom: '1rem', color: '#dee2e6' }}></i>
            <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>No reports configured</p>
            <p style={{ fontSize: '0.95rem' }}>Click "Add Report" to create your first automated report.</p>
          </div>
        ) : (
          <DataTable 
            value={reports} 
            emptyMessage="No reports found"
            paginator
            rows={10}
            rowsPerPageOptions={[5, 10, 20]}
            style={{ width: '100%' }}
          >
            <Column field="name" header="Report Name" sortable style={{ minWidth: '200px' }} />
            <Column body={statusBodyTemplate} header="Status" style={{ minWidth: '120px' }} />
            <Column body={emailsBodyTemplate} header="Emails" style={{ minWidth: '150px' }} />
            <Column body={formatFrequency} header="Schedule" style={{ minWidth: '200px' }} />
            <Column body={actionsBodyTemplate} header="Actions" style={{ minWidth: '180px' }} />
          </DataTable>
        )}
      </Card>

      {/* Report Dialog */}
      <Dialog
        header={editingReport ? 'Edit Report' : 'Create New Report'}
        visible={dialogVisible}
        style={{ width: '90vw', maxWidth: '800px' }}
        onHide={() => {
          setDialogVisible(false);
          resetForm();
        }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1rem' }}>
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => {
                setDialogVisible(false);
                resetForm();
              }}
              className="p-button-text p-button-lg"
              disabled={saving}
              style={{ 
                padding: '0.75rem 1.5rem',
                fontSize: '1rem'
              }}
            />
            <Button
              label={editingReport ? 'Update' : 'Create'}
              icon={saving ? null : (editingReport ? "pi pi-check" : "pi pi-plus")}
              iconPos="left"
              onClick={handleSave}
              disabled={saving}
              loading={saving}
              className="p-button-lg"
              style={{ 
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                fontWeight: '500',
                minWidth: '140px'
              }}
            />
          </div>
        }
      >
        <div style={{ padding: '1rem 0' }}>
          {/* Report Name */}
          <div className="p-field" style={{ marginBottom: '1.5rem' }}>
            <label className="p-d-block" style={{ marginBottom: '0.75rem', fontWeight: '600', color: '#495057', fontSize: '0.95rem' }}>
              Report Name *
            </label>
            <InputText
              value={reportForm.name}
              onChange={(e) => setReportForm({ ...reportForm, name: e.target.value })}
              style={{ width: '100%', padding: '0.75rem', fontSize: '1rem' }}
              placeholder="e.g., Daily User Activity Report"
            />
          </div>

          {/* Enabled Toggle */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            padding: '1rem',
            backgroundColor: reportForm.enabled ? '#e8f5e9' : '#f8f9fa',
            borderRadius: '8px',
            marginBottom: '1.5rem'
          }}>
            <div>
              <label style={{ fontWeight: '600', color: reportForm.enabled ? '#2e7d32' : '#495057', fontSize: '1rem', display: 'block', marginBottom: '0.25rem' }}>
                {reportForm.enabled ? 'Automation Enabled' : 'Automation Disabled'}
              </label>
              <p style={{ margin: 0, color: '#6c757d', fontSize: '0.9rem' }}>
                {reportForm.enabled ? 'Reports will be sent automatically' : 'Reports are currently disabled'}
              </p>
            </div>
            <InputSwitch
              checked={reportForm.enabled}
              onChange={(e) => setReportForm({ ...reportForm, enabled: e.value })}
            />
          </div>

          {reportForm.enabled && (
            <>
              {/* Email Addresses */}
              <div className="p-field" style={{ marginBottom: '1.5rem' }}>
                <label className="p-d-block" style={{ marginBottom: '0.75rem', fontWeight: '600', color: '#495057', fontSize: '0.95rem' }}>
                  Email Addresses * (one per line or comma-separated)
                </label>
                <textarea
                  value={reportForm.emailsText}
                  onChange={(e) => setReportForm({ ...reportForm, emailsText: e.target.value })}
                  rows={4}
                  style={{ 
                    width: '100%', 
                    fontSize: '1rem',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    border: '1px solid #ced4da',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                  placeholder="email1@example.com&#10;email2@example.com&#10;email3@example.com"
                />
                <small style={{ display: 'block', marginTop: '0.5rem', color: '#6c757d', fontSize: '0.85rem' }}>
                  Enter email addresses, one per line or separated by commas
                </small>
              </div>

              {/* Frequency */}
              <div className="p-field" style={{ marginBottom: '1.5rem' }}>
                <label className="p-d-block" style={{ marginBottom: '0.75rem', fontWeight: '600', color: '#495057', fontSize: '0.95rem' }}>
                  Frequency *
                </label>
                <Dropdown
                  value={reportForm.frequency}
                  options={frequencyOptions}
                  onChange={(e) => setReportForm({ ...reportForm, frequency: e.value })}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Select frequency"
                  style={{ width: '100%' }}
                />
              </div>

              {/* Day of Week (Weekly) */}
              {reportForm.frequency === 'weekly' && (
                <div className="p-field" style={{ marginBottom: '1.5rem' }}>
                  <label className="p-d-block" style={{ marginBottom: '0.75rem', fontWeight: '600', color: '#495057', fontSize: '0.95rem' }}>
                    Day of Week *
                  </label>
                  <Dropdown
                    value={reportForm.dayOfWeek}
                    options={dayOfWeekOptions}
                    onChange={(e) => setReportForm({ ...reportForm, dayOfWeek: e.value })}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select day"
                    style={{ width: '100%' }}
                  />
                </div>
              )}

              {/* Day of Month (Monthly) */}
              {reportForm.frequency === 'monthly' && (
                <div className="p-field" style={{ marginBottom: '1.5rem' }}>
                  <label className="p-d-block" style={{ marginBottom: '0.75rem', fontWeight: '600', color: '#495057', fontSize: '0.95rem' }}>
                    Day of Month * (1-31)
                  </label>
                  <Dropdown
                    value={reportForm.dayOfMonth}
                    options={dayOfMonthOptions}
                    onChange={(e) => setReportForm({ ...reportForm, dayOfMonth: e.value })}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select day"
                    style={{ width: '100%' }}
                  />
                </div>
              )}

              {/* Time */}
              <div className="p-field" style={{ marginBottom: '1.5rem' }}>
                <label className="p-d-block" style={{ marginBottom: '0.75rem', fontWeight: '600', color: '#495057', fontSize: '0.95rem' }}>
                  Send Time * (24-hour format)
                </label>
                <InputText
                  type="time"
                  value={reportForm.time}
                  onChange={(e) => setReportForm({ ...reportForm, time: e.target.value })}
                  style={{ width: '100%', padding: '0.75rem', fontSize: '1rem' }}
                />
                <small style={{ display: 'block', marginTop: '0.5rem', color: '#6c757d', fontSize: '0.85rem' }}>
                  Time when reports will be sent (HH:mm format, e.g., 09:00 for 9:00 AM)
                </small>
              </div>
            </>
          )}

          {/* Report Options */}
          <div style={{ 
            marginTop: '2rem',
            paddingTop: '1.5rem',
            borderTop: '1px solid #e9ecef'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem', fontWeight: '600', color: '#2c3e50' }}>
              Report Content Options
            </h3>
            <p style={{ marginBottom: '1.5rem', color: '#6c757d', fontSize: '0.9rem' }}>
              Select what information to include in the report:
            </p>

            <div style={{ display: 'grid', gap: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <label style={{ fontWeight: '500', color: '#495057', fontSize: '0.95rem', cursor: 'pointer' }}>
                  Include PDF Attachment
                </label>
                <InputSwitch
                  checked={reportForm.includePDF}
                  onChange={(e) => setReportForm({ ...reportForm, includePDF: e.value })}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <label style={{ fontWeight: '500', color: '#495057', fontSize: '0.95rem', cursor: 'pointer' }}>
                  Include Summary Statistics
                </label>
                <InputSwitch
                  checked={reportForm.includeSummary}
                  onChange={(e) => setReportForm({ ...reportForm, includeSummary: e.value })}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <label style={{ fontWeight: '500', color: '#495057', fontSize: '0.95rem', cursor: 'pointer' }}>
                  Include Log Level Breakdown
                </label>
                <InputSwitch
                  checked={reportForm.includeLevelBreakdown}
                  onChange={(e) => setReportForm({ ...reportForm, includeLevelBreakdown: e.value })}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <label style={{ fontWeight: '500', color: '#495057', fontSize: '0.95rem', cursor: 'pointer' }}>
                  Include Top Active Users
                </label>
                <InputSwitch
                  checked={reportForm.includeTopUsers}
                  onChange={(e) => setReportForm({ ...reportForm, includeTopUsers: e.value })}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <label style={{ fontWeight: '500', color: '#495057', fontSize: '0.95rem', cursor: 'pointer' }}>
                  Include Top Modules/Categories
                </label>
                <InputSwitch
                  checked={reportForm.includeTopModules}
                  onChange={(e) => setReportForm({ ...reportForm, includeTopModules: e.value })}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <label style={{ fontWeight: '500', color: '#495057', fontSize: '0.95rem', cursor: 'pointer' }}>
                  Include Error Statistics
                </label>
                <InputSwitch
                  checked={reportForm.includeErrorStats}
                  onChange={(e) => setReportForm({ ...reportForm, includeErrorStats: e.value })}
                />
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      <ConfirmDialog />
    </div>
  );
};

export default Automation;
